import { useReveal } from "@/hooks/useReveal";
import { Shirt, CalendarX, ShoppingBag, EyeOff } from "lucide-react";

export default function Marquee() {
    const phrases = [
        "Imagen estratégica",
        "Estilo real",
        "Presencia digital",
        "Confianza al vestir",
        "Personal Branding",
        "Coherencia",
        "Styling editorial",
    ];
    const items = [...phrases, ...phrases];

    return (
        <section
            data-testid="marquee-section"
            aria-hidden="false"
            className="relative py-10 border-y border-[#1E1E1E]/10 overflow-hidden bg-[#F7F3EE]"
        >
            <div className="marquee-track">
                {items.map((p, i) => (
                    <div key={i} className="flex items-center gap-16 shrink-0">
                        <span className="font-display italic text-[28px] md:text-[40px] text-[#1E1E1E]/85">
                            {p}
                        </span>
                        <span className="w-2 h-2 rotate-45 bg-[#C6A76A] shrink-0" />
                    </div>
                ))}
            </div>
        </section>
    );
}

export function Problema() {
    const { ref, visible } = useReveal();
    const points = [
        { text: "Tu clóset ya no te representa", Icon: Shirt },
        { text: "No sabes qué ponerte para eventos importantes", Icon: CalendarX },
        { text: "Compras ropa, pero no la usas", Icon: ShoppingBag },
        { text: "No proyectas lo que realmente eres", Icon: EyeOff },
    ];

    return (
        <section
            id="problema"
            data-testid="problema-section"
            className="relative py-24 md:py-32"
        >
            <div className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16">
                {/* Centered header */}
                <div className="text-center max-w-3xl mx-auto">
                    <div className="inline-flex items-center justify-center gap-3 mb-6">
                        <span className="h-px w-10 bg-[#8C7C72]" />
                        <span className="section-number">Problema</span>
                        <span className="h-px w-10 bg-[#8C7C72]" />
                    </div>
                    <h2
                        data-testid="problema-title"
                        className="font-display text-[44px] md:text-[64px] leading-[1.05] text-[#1E1E1E] tracking-[-0.02em]"
                    >
                        ¿Te pasa{" "}
                        <span className="font-italic-display text-[#C6A76A]">esto?</span>
                    </h2>
                    <p className="mt-6 text-[#1E1E1E]/65 leading-relaxed max-w-xl mx-auto">
                        La imagen que muestras hoy no siempre cuenta la historia de quién eres realmente. Estas son las señales más comunes.
                    </p>
                </div>

                {/* 4 columns */}
                <div
                    ref={ref}
                    className={`mt-16 md:mt-20 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-7 reveal ${visible ? "is-visible" : ""}`}
                >
                    {points.map(({ text, Icon }, i) => (
                        <div
                            key={text}
                            data-testid={`problema-item-${i}`}
                            className="group relative p-8 md:p-9 bg-[#F7F3EE] border border-[#1E1E1E]/10 hover:border-[#C6A76A]/60 hover:-translate-y-1 transition-all duration-500"
                        >
                            <div className="flex items-center justify-center w-14 h-14 rounded-full bg-[#D8B9A7]/40 group-hover:bg-[#C6A76A]/40 transition-colors duration-500 mb-6">
                                <Icon
                                    size={24}
                                    strokeWidth={1.25}
                                    className="text-[#1E1E1E] group-hover:text-[#1E1E1E] transition-colors"
                                />
                            </div>
                            <p className="font-display text-xl md:text-[22px] leading-tight text-[#1E1E1E] tracking-tight">
                                {text}
                            </p>
                            <span className="absolute bottom-0 left-0 h-[2px] w-0 bg-[#C6A76A] group-hover:w-full transition-all duration-700" />
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
}
